
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProviderAccess } from "@/hooks/useProviderAccess";
import { useAccessCodes, useCreateAccessCode } from "@/hooks/useAccessCodes";
import { useCheckOut } from "@/hooks/useCheckOut";
import { useBookingCancellation } from "@/hooks/useBookingCancellation";
import { useRefundBooking } from "@/hooks/useRefundBooking";
import { useRefundHistory } from "@/hooks/useRefundHistory";
import { useBookingPaymentDetails } from "@/hooks/useBookingPaymentDetails";
import { useAuth } from "@/hooks/useAuth";
import { useBookingCheckInLogic } from "@/hooks/useBookingCheckInLogic";
import { useBookingTimeCalculations } from "@/hooks/useBookingTimeCalculations";
import ProviderHeader from "@/components/ProviderHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Calendar, Clock, User, Mail, Phone, Package, CreditCard, MapPin, FileText, RefreshCw, CheckCircle2, Key, Tag, Ticket, Gift } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { utcToLocalDateTime } from "@/utils/timezoneUtils";
import { RefundHistorySection } from "@/components/booking/RefundHistorySection";
import { BookingPaymentInfo } from "@/components/booking/BookingPaymentInfo";

interface BookingDetail {
  id: string;
  booking_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  price: number;
  currency: string;
  status: string;
  notes?: string;
  checked_in_at?: string;
  checked_out_at?: string;
  created_at: string;
  updated_at: string;
  stripe_session_id?: string;
  service: {
    name: string;
    service_type: string;
    description?: string;
  };
  provider: {
    name: string;
    address?: string;
    blur_customer_emails?: boolean;
    early_access_enabled?: boolean;
    early_access_minutes?: number;
  };
}

// Mobile Safari detection utility
const isMobileSafari = () => {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua);
  const standalone = (window.navigator as any).standalone;
  return iOS && webkit && !standalone;
};

const ProviderBookingDetail = () => {
  const { bookingNumber, bookingId } = useParams<{ bookingNumber?: string; bookingId?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const { data: providerAccess, isLoading: accessLoading } = useProviderAccess();
  const checkOut = useCheckOut();
  const refundBooking = useRefundBooking();

  // Mobile Safari debugging
  const isMobile = isMobileSafari();
  
  console.log('=== PROVIDER BOOKING DETAIL AUTH DEBUG ===');
  console.log('Is Mobile Safari:', isMobile);
  console.log('Auth loading:', authLoading);
  console.log('User:', user?.email);
  console.log('Provider access loading:', accessLoading);
  console.log('Provider access data:', providerAccess);
  console.log('Booking params:', { bookingNumber, bookingId });
  console.log('User agent:', navigator.userAgent);
  console.log('===========================================');

  const { data: booking, isLoading, error } = useQuery({
    queryKey: ['provider-booking-detail', bookingNumber, bookingId],
    queryFn: async () => {
      let query = supabase
        .from('bookings')
        .select(`
          *,
          service:services(
            name,
            service_type,
            description
          ),
          provider:providers(name, address, blur_customer_emails, early_access_enabled, early_access_minutes)
        `);

      // Use bookingId if available (from /management/booking/id/:bookingId route)
      // Otherwise use bookingNumber (from /management/booking/:bookingNumber route)
      if (bookingId) {
        console.log('Fetching booking by ID:', bookingId);
        query = query.eq('id', bookingId);
      } else if (bookingNumber) {
        console.log('Fetching booking by number:', bookingNumber);
        query = query.eq('booking_number', bookingNumber);
      } else {
        throw new Error("Neither booking ID nor booking number provided");
      }

      const { data, error } = await query.single();

      if (error) {
        console.error('Error fetching booking:', error);
        throw error;
      }
      
      console.log('Booking fetched successfully:', data);
      console.log('Provider blur setting:', data.provider?.blur_customer_emails);
      console.log('Booking check-in/out timestamps:', {
        checked_in_at: data.checked_in_at,
        checked_out_at: data.checked_out_at
      });
      return data as BookingDetail;
    },
    // CRITICAL: Include auth loading state to prevent premature queries
    enabled: !!(bookingNumber || bookingId) && !authLoading && !!user,
  });

  // Fetch complete provider data for early access settings
  const { data: fullProvider } = useQuery({
    queryKey: ['provider-full', booking?.id],
    queryFn: async () => {
      if (!booking?.id) return null;
      
      // Get provider_id from the booking's provider_id field in the database
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select('provider_id')
        .eq('id', booking.id)
        .single();
        
      if (bookingError || !bookingData) {
        console.error('Error fetching booking provider_id:', bookingError);
        return null;
      }
      
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .eq('id', bookingData.provider_id)
        .single();
        
      if (error) {
        console.error('Error fetching full provider data:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!booking?.id,
  });

  // Fetch access codes for this booking
  const { data: accessData, isLoading: accessCodesLoading } = useAccessCodes(booking?.id);
  
  // Create access code mutation
  const createAccessCode = useCreateAccessCode();

  // Fetch refund history for this booking
  const { data: refundHistory, isLoading: refundHistoryLoading } = useRefundHistory(booking?.id);

  // Fetch payment details (coupons and membership info)
  const { data: paymentDetails, isLoading: paymentDetailsLoading } = useBookingPaymentDetails(booking?.id);

  console.log('Payment details data:', paymentDetails);

  // Extract access codes and device info from the combined response
  const accessCodesArray = accessData?.accessCodes || [];
  const deviceInfo = accessData?.devices || [];
  
  // Check if there are enabled devices
  const hasEnabledDevices = deviceInfo.some(device => device.is_enabled && device.seam_device_id);
  
  // Get current access code
  const currentAccessCode = accessCodesArray.find(code => code.is_active);
  
  // Use booking time calculations to get proper check-in availability
  const timeCalculations = useBookingTimeCalculations({
    booking: booking,
    currentTime: new Date(),
    checkInWindowMinutes: fullProvider?.check_in_window_minutes || 5,
    isCompleted: booking?.status.toLowerCase() === 'completed',
    isCancelled: booking?.status.toLowerCase() === 'cancelled',
    isCheckedIn: !!booking?.checked_in_at
  });
  
  
  // Debug the time calculations
  console.log('=== CHECK IN AVAILABILITY DEBUG ===');
  console.log('Current time:', new Date().toISOString());
  console.log('Check in available time from hook:', timeCalculations.checkInAvailableTime?.toISOString());
  console.log('Provider check in window minutes:', fullProvider?.check_in_window_minutes);
  console.log('Booking data for calculations:', {
    booking_date: booking?.booking_date,
    start_time: booking?.start_time,
    end_time: booking?.end_time,
    status: booking?.status,
    checked_in_at: booking?.checked_in_at
  });
  
  // Determine if check-in is available using the hook's calculations
  const canCheckIn = booking && 
    booking.status.toLowerCase() === 'confirmed' && 
    !booking.checked_in_at &&
    timeCalculations.checkInAvailableTime !== null &&
    new Date() >= timeCalculations.checkInAvailableTime;
    
  console.log('Can check in result:', canCheckIn);
  console.log('===================================');

  // Use booking check-in logic hook with full provider data
  const { handleCheckIn: handleBookingCheckIn } = useBookingCheckInLogic({
    booking,
    devices: deviceInfo,
    currentAccessCode,
    createAccessCode,
    hasEnabledDevices,
    canCheckIn: canCheckIn || false,
    provider: fullProvider || booking?.provider // Use full provider data if available, fallback to booking provider
  });

  // Check if refund already exists
  const hasExistingRefund = refundHistory && refundHistory.length > 0;

  // Check if booking was created by provider
  const isProviderCreated = booking?.notes && (
    booking.notes.includes('Booking created by provider') ||
    booking.notes.includes('provider will need to sign up') ||
    booking.notes.includes('Customer will need to sign up')
  );

  console.log('Provider created booking check:', {
    bookingId: booking?.id,
    notes: booking?.notes,
    isProviderCreated: isProviderCreated
  });

  // Helper function to format timestamp with friendly formatting
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const isSameDay = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
      
      if (isSameDay) {
        return `Today at ${format(date, 'h:mm a')}`;
      } else {
        return `${format(date, 'do MMMM')} at ${format(date, 'h:mm a')}`;
      }
    } catch (error) {
      console.log('Could not parse timestamp, returning original:', timestamp);
      return timestamp;
    }
  };

  const blurEmailExtension = (email: string) => {
    const [localPart, domain] = email.split('@');
    if (!domain) return email;
    
    return (
      <>
        {localPart}@
        <span className="blur-sm select-none">{domain}</span>
      </>
    );
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!booking) return;

    try {
      const { error } = await supabase
        .from('bookings')
        .update({ 
          status: newStatus, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', booking.id);

      if (error) throw error;

      toast({
        title: "Booking Updated",
        description: `Booking status changed to ${newStatus}`,
      });

      // Refresh the booking data
      window.location.reload();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: "Error",
        description: "Failed to update booking status",
        variant: "destructive",
      });
    }
  };

  const handleCheckIn = async () => {
    if (!booking) return;

    try {
      // If devices are enabled, use the booking check-in logic which handles access code creation
      if (hasEnabledDevices) {
        await handleBookingCheckIn();
      } else {
        // Manual check-in without access code creation
        const { error } = await supabase
          .from('bookings')
          .update({ 
            status: 'confirmed',
            checked_in_at: new Date().toISOString(),
            updated_at: new Date().toISOString() 
          })
          .eq('id', booking.id);

        if (error) throw error;

        toast({
          title: "Customer Checked In",
          description: "Customer has been successfully checked in manually",
        });

        // Refresh the booking data
        window.location.reload();
      }
    } catch (error) {
      console.error('Error checking in customer:', error);
      toast({
        title: "Error",
        description: "Failed to check in customer",
        variant: "destructive",
      });
    }
  };

  const handleCheckOut = async () => {
    if (!booking) return;

    try {
      await checkOut.mutateAsync(booking.id);
    } catch (error) {
      console.error('Check-out failed:', error);
    }
  };

  const handleRefund = async () => {
    if (!booking?.stripe_session_id) {
      toast({
        title: "Cannot Process Refund",
        description: "No payment information found for this booking.",
        variant: "destructive",
      });
      return;
    }

    try {
      await refundBooking.mutateAsync(booking.id);
    } catch (error) {
      console.error('Refund failed:', error);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'EEEE, do MMMM yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString: string) => {
    try {
      // FIXED: Convert from UTC time to local time for display
      // Create a proper UTC ISO string
      const currentDate = booking?.booking_date || new Date().toISOString().split('T')[0];
      const utcTimeString = `${currentDate}T${timeString}Z`;

      // Use timezone utility to convert to local time
      const localTime = utcToLocalDateTime(utcTimeString);
      
      console.log('Time conversion in formatTime:', {
        originalUtcTime: timeString,
        fullUtcString: utcTimeString,
        localTimeDisplay: localTime.localTimeDisplay
      });
      
      return localTime.localTimeDisplay;
    } catch (error) {
      console.error('Error formatting time:', error, timeString);
      return timeString;
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString);
      const isSameDay = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
      
      if (isSameDay) {
        return `Today at ${format(date, 'h:mm a')}`;
      } else {
        return `${format(date, 'do MMMM yyyy')} at ${format(date, 'h:mm a')}`;
      }
    } catch (error) {
      return dateTimeString;
    }
  };

  // Updated function to get the correct status based on booking state using new timestamp columns
  const getStatusBadge = () => {
    if (!booking) return null;

    const normalizedStatus = booking.status.toLowerCase();
    
    // Check if cancelled or completed first (these take priority)
    if (normalizedStatus === 'cancelled') {
      return <Badge className="bg-red-100 text-red-800 border-red-200 whitespace-nowrap">Cancelled</Badge>;
    }
    
    if (normalizedStatus === 'completed') {
      return <Badge className="bg-green-100 text-green-800 border-green-200 whitespace-nowrap">Completed</Badge>;
    }
    
    // Check if customer is checked in using new timestamp column
    const isCheckedIn = !!booking.checked_in_at;
    if (isCheckedIn) {
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200 whitespace-nowrap">In Session</Badge>;
    }
    
    // Default status based on database value
    switch (normalizedStatus) {
      case 'confirmed':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200 whitespace-nowrap">Confirmed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 whitespace-nowrap">Pending</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200 whitespace-nowrap">{booking.status}</Badge>;
    }
  };

  // UPDATED: Include auth loading state in overall loading condition
  // This prevents rendering before auth state is fully determined
  if (authLoading || accessLoading || isLoading) {
    console.log('Loading states:', { authLoading, accessLoading, isLoading });
    
    return (
      <div className="min-h-screen bg-gray-50">
        <ProviderHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
            {isMobile && (
              <div className="text-center text-sm text-blue-600">
                Loading on mobile device... Please wait.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // If auth has finished loading but no user found, don't show error immediately on mobile
  // This gives more time for the auth state to settle
  if (!authLoading && !user) {
    console.log('No user found after auth loading completed');
    
    if (isMobile) {
      console.log('Mobile Safari detected - showing extended loading');
      return (
        <div className="min-h-screen bg-gray-50">
          <ProviderHeader />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p className="text-gray-600">Authenticating... Please wait a moment.</p>
            </div>
          </div>
        </div>
      );
    }
  }

  if (error || !booking) {
    console.error('Booking detail error:', error);
    return (
      <div className="min-h-screen bg-gray-50">
        <ProviderHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="text-center">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Booking Not Found</h1>
            <p className="text-gray-600 mb-4 text-sm sm:text-base">The booking you're looking for doesn't exist or you don't have permission to view it.</p>
            <p className="text-gray-500 text-sm mb-4">Searched for: {bookingId ? `ID ${bookingId}` : `Number ${bookingNumber}`}</p>
            <Button onClick={() => navigate('/management')} size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const normalizedStatus = booking.status.toLowerCase();
  const canConfirm = normalizedStatus === 'pending';
  const canCancelBooking = normalizedStatus === 'confirmed' || normalizedStatus === 'pending';
  // Only show refund button if there's a stripe session ID, booking is eligible, AND no refund exists yet
  const canRefund = booking.stripe_session_id && (normalizedStatus === 'confirmed' || normalizedStatus === 'completed') && !hasExistingRefund;
  const canCheckInProvider = normalizedStatus === 'confirmed' && !booking.checked_in_at;
  const canCheckOut = booking.checked_in_at && normalizedStatus !== 'completed';

  const isCheckedIn = !!booking.checked_in_at;
  const checkInTime = booking.checked_in_at ? formatTimestamp(booking.checked_in_at) : null;
  const checkOutTime = booking.checked_out_at ? formatTimestamp(booking.checked_out_at) : null;

  console.log('Check-in analysis:', {
    isCheckedIn,
    checkInTime,
    checkOutTime,
    checked_in_at: booking.checked_in_at,
    checked_out_at: booking.checked_out_at
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <ProviderHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Back Button */}
        <div className="mb-4 sm:mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/management')}
            className="inline-flex items-center text-sm"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 truncate">
              Booking #{booking.booking_number}
            </h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Complete booking details</p>
          </div>
          <div className="flex-shrink-0">
            {getStatusBadge()}
          </div>
        </div>

        {/* Quick Actions - Only show if there are actions available */}
        {(canCheckInProvider || canCheckOut || canConfirm) && (
          <div className="mb-6">
            <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-lg">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-blue-600" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-3">
                  {canCheckInProvider && (
                    <Button
                      onClick={handleCheckIn}
                      disabled={createAccessCode.isPending}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {createAccessCode.isPending ? "Checking In..." : "Check In"}
                    </Button>
                  )}
                  
                  {canCheckOut && (
                    <Button
                      onClick={handleCheckOut}
                      disabled={checkOut.isPending}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      size="sm"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {checkOut.isPending ? "Checking Out..." : "Check Out"}
                    </Button>
                  )}
                  
                  {canConfirm && (
                    <Button
                      onClick={() => handleStatusUpdate('confirmed')}
                      variant="outline"
                      size="sm"
                    >
                      Confirm Booking
                    </Button>
                  )}
                </div>
                {canCheckInProvider && (
                  <p className="text-xs text-gray-600">
                    {hasEnabledDevices 
                      ? "Check in the customer and generate access code for self-service entry."
                      : "Check in the customer - manual entry required (no self-service devices active)."
                    }
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Check-in/Check-out and Access Codes Section */}
        {isCheckedIn && (
          <div className="mb-6">
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="flex items-center text-lg sm:text-xl">
                  <Key className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Access Control & Check-in Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                {/* Check-in/Check-out Times */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {checkInTime && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">Check-in Time</span>
                      </div>
                      <p className="text-sm text-green-700">{checkInTime}</p>
                    </div>
                  )}
                  
                  {checkOutTime && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">Check-out Time</span>
                      </div>
                      <p className="text-sm text-blue-700">{checkOutTime}</p>
                    </div>
                  )}
                </div>

                {/* Access Codes */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Access Codes</h4>
                  {accessCodesLoading ? (
                    <div className="text-sm text-gray-500">Loading access codes...</div>
                  ) : accessCodesArray && accessCodesArray.length > 0 ? (
                    <div className="space-y-2">
                      {accessCodesArray.map((accessCode) => {
                        // Check if the access code has expired based on booking end time
                        const now = new Date();
                        const bookingEndTime = new Date(`${booking.booking_date}T${booking.end_time}Z`);
                        const isExpired = now > bookingEndTime;
                        
                        // Determine the status and variant
                        let status = 'Inactive';
                        let variant: 'default' | 'secondary' | 'destructive' = 'secondary';
                        
                        if (isExpired) {
                          status = 'Expired';
                          variant = 'destructive';
                        } else if (accessCode.is_active) {
                          status = 'Active';
                          variant = 'default';
                        }
                        
                        return (
                          <div key={accessCode.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-lg font-mono font-bold text-gray-900">{accessCode.code}</p>
                                <p className="text-xs text-gray-500">
                                  Valid from: {format(new Date(accessCode.start_time), 'do MMMM yyyy, h:mm a')}
                                </p>
                                {isExpired && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Expired at: {format(bookingEndTime, 'do MMMM yyyy, h:mm a')}
                                  </p>
                                )}
                              </div>
                              <Badge variant={variant}>
                                {status}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      No access codes generated for this booking.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-4 sm:space-y-6">
            {/* Customer Information */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="flex items-center text-lg sm:text-xl">
                  <User className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 pt-0">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Name</label>
                  <p className="text-base sm:text-lg font-semibold break-words">{booking.customer_name}</p>
                </div>
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm sm:text-base break-all">
                    {booking.provider?.blur_customer_emails !== false ? blurEmailExtension(booking.customer_email) : booking.customer_email}
                  </span>
                </div>
                {booking.customer_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <span className="text-sm sm:text-base">{booking.customer_phone}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Service Information */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="flex items-center text-lg sm:text-xl">
                  <Package className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Service Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 pt-0">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Service</label>
                  <p className="text-base sm:text-lg font-semibold break-words">{booking.service.name}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Type</label>
                  <p className="capitalize text-sm sm:text-base">{booking.service.service_type}</p>
                </div>
                {booking.service.description && (
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">Description</label>
                    <p className="text-gray-700 text-sm sm:text-base">{booking.service.description}</p>
                  </div>
                )}
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Duration</label>
                  <p className="text-sm sm:text-base">{booking.duration_minutes} minutes</p>
                </div>
              </CardContent>
            </Card>

            {/* Booking Notes */}
            {booking.notes && (
              <Card>
                <CardHeader className="pb-3 sm:pb-6">
                  <CardTitle className="flex items-center text-lg sm:text-xl">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    Notes
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-gray-700 text-sm sm:text-base whitespace-pre-wrap">{booking.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Date & Time */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="flex items-center text-lg sm:text-xl">
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Date & Time
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 pt-0">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Date</label>
                  <p className="font-semibold text-sm sm:text-base break-words">{formatDate(booking.booking_date)}</p>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm sm:text-base">{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Payment Information - UPDATED to pass paymentDetails */}
            <BookingPaymentInfo
              price={booking.price}
              canCancel={canCancelBooking}
              cancelPending={refundBooking.isPending}
              onCancel={handleRefund}
              isProviderCreated={isProviderCreated}
              paymentDetails={paymentDetails}
              isLoading={paymentDetailsLoading}
              hideActions={true} // Hide cancel button in provider view
            />

            {/* Location */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="flex items-center text-lg sm:text-xl">
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Location
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="font-semibold text-sm sm:text-base break-words">{booking.provider.name}</p>
                {booking.provider.address && (
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">{booking.provider.address}</p>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="text-lg sm:text-xl">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 sm:space-y-3 pt-0">
                {canCancelBooking && (
                  <Button
                    onClick={() => handleStatusUpdate('cancelled')}
                    variant="destructive"
                    className="w-full text-sm"
                    size="sm"
                  >
                    Cancel Booking
                  </Button>
                )}
                {canRefund && (
                  <Button
                    onClick={handleRefund}
                    disabled={refundBooking.isPending}
                    variant="outline"
                    className="w-full text-sm"
                    size="sm"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {refundBooking.isPending ? "Processing Refund..." : "Process Refund"}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Booking Meta */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="text-lg sm:text-xl">Booking Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs sm:text-sm pt-0">
                <div>
                  <span className="text-gray-500">Created:</span>
                  <p className="break-words">{formatDateTime(booking.created_at)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Last Updated:</span>
                  <p className="break-words">{formatDateTime(booking.updated_at)}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* NEW: Refund History Section */}
        {refundHistory && refundHistory.length > 0 &&  (
          <div className="mt-6 lg:mt-8">
            <RefundHistorySection 
              refunds={refundHistory} 
              isLoading={refundHistoryLoading} 
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderBookingDetail;
